import React from 'react';
import {
  flexRender,
  type Table as ReactTable,
  type Row,
} from '@tanstack/react-table';

import Loader from 'components/partials/loader/loader';
import TableRow from 'components/partials/table-component/table/table-row';

import { getSortIcon } from 'components/partials/table-component/helpers/get-sort-icon';
import {
  getAriaSort,
  getSortStateLabel,
} from 'components/partials/table-component/helpers/get-sort-aria';

interface SortingParam {
  id: string;
  desc: boolean;
}

interface TableProps {
  table: ReactTable<unknown>;
  sortingParams: SortingParam[];
  isLoading: boolean;
  isMultiSort: boolean;
  customHeight: string;
  expandable?: boolean;
  renderSubComponent?: (props: { row: Row<unknown> }) => React.ReactNode;
  getRowClassName?: (row: unknown, index: number) => string;
  /** Applied to both the header and body `<table>` (this component splits the
   * table in two so the header can stay fixed above a scrollable body). */
  ariaLabel?: string;
}

export const Table = ({
  table,
  sortingParams,
  isLoading,
  isMultiSort,
  customHeight,
  expandable = false,
  renderSubComponent,
  getRowClassName,
  ariaLabel,
}: TableProps): React.JSX.Element => (
  <>
    <table
      className="w-full border-separate border-spacing-0 table-fixed"
      aria-label={ariaLabel}
    >
      <thead data-testid="table-header">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header, _headerInd) => {
              const isColumnSorted = header.column.getSortIndex() >= 0;
              const bgColor = isColumnSorted
                ? 'bg-navy-500 text-white'
                : 'bg-navy-50 text-navy-600';

              const headerPositionClass = isMultiSort
                ? 'justify-between'
                : header.column.columnDef.isHeaderCentered && 'justify-center';

              const isSortable =
                header.column.columnDef.enableSorting !== false;

              const sortedItem = sortingParams.find(
                (sort) => sort.id === header.column.columnDef.id
              );
              const ariaSort = getAriaSort(isSortable, sortedItem);
              const sortStateLabel = getSortStateLabel(sortedItem);

              return (
                <th
                  key={header.id}
                  className={`tracking-[1.2px] ${header.column.columnDef.className} ${bgColor} font-xs ${header.column.columnDef.headerClassName}`}
                  data-testid={`table-header-cell-${header.column.columnDef.id}`}
                  scope="col"
                  aria-sort={ariaSort}
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      className="w-full bg-transparent border-none cursor-pointer text-inherit p-0"
                      aria-label={`Sort by ${
                        header.column.columnDef.meta?.title ||
                        header.column.columnDef.id
                      }, ${sortStateLabel}`}
                      data-testid={`table-header-cell-button-${header.column.columnDef.id}`}
                    >
                      <div
                        className={`flex items-center ${headerPositionClass}`}
                      >
                        <div className="w-100 flex justify-center">
                          <div key={`${header.id}-label`} className="pr-1">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                          {getSortIcon(
                            sortingParams,
                            header.column.columnDef.id ?? '',
                            header.column.columnDef.enableSorting
                          )}
                        </div>
                        {isMultiSort && isColumnSorted && (
                          <div className="pr-4">
                            {header.column.getSortIndex() + 1}
                          </div>
                        )}
                      </div>
                    </button>
                  ) : (
                    <div className={`flex items-center ${headerPositionClass}`}>
                      <div className="w-100 flex justify-center">
                        <div key={`${header.id}-label`} className="pr-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
    </table>

    <div className={`overflow-y-auto mt-2 custom-scroll ${customHeight}`}>
      <table
        className="w-full table-fixed border-separate border-spacing-0 pr-0.5"
        aria-label={ariaLabel}
      >
        <tbody data-testid="table-body">
          {table.getRowModel().rows.map((row, rowInd) => (
            <TableRow
              key={row.id}
              row={row}
              rowInd={rowInd}
              rowsLength={table.getRowModel().rows.length}
              expandable={expandable}
              renderSubComponent={renderSubComponent}
              rowClassName={getRowClassName?.(row.original, rowInd)}
            />
          ))}
        </tbody>
      </table>
    </div>

    <Loader
      {...{ loading: isLoading }}
      fullScreen={false}
      customClass="!bg-transparent"
    />
  </>
);
